import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";
import { Checkbox, DateInput, Input, Select } from "shared/components/forms";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { DiscountCode, CustomDiscount } from "views/discounts/types";
import { DiscountRadioCard } from "./DiscountRadioCard";
import { useAuthContext } from "providers/Auth";
import { DiscountType } from "shared/types";
import { Tabs } from "shared/components/Tabs";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

const discountTypeOptions = [
  { value: "flat", label: "Flat" },
  { value: "percentage", label: "Percentage of application fee" },
  { value: "free", label: "Free" }
];

type ApplyDiscountModalProps = {
  opened: boolean;
  onClose: () => void;
  applicantEmail: string;
  activeDiscounts: DiscountCode[];
  onApplyDiscount: (discount: DiscountCode | CustomDiscount) => void;
};

export const ApplyDiscountModal = ({
  opened,
  onClose,
  applicantEmail,
  activeDiscounts,
  onApplyDiscount
}: ApplyDiscountModalProps) => {
  const { permissions } = useAuthContext();
  const [activeTab, setActiveTab] = useState<"active" | "custom">("active");
  const [selectedActiveDiscount, setSelectedActiveDiscount] =
    useState<DiscountCode | null>(null);
  const [customDiscount, setCustomDiscount] = useState({
    code: `CUSTOM-${Date.now()}`,
    type: "flat" as DiscountType,
    value: "" as number | string,
    startDate: new Date().toISOString().split("T")[0],
    endDate: "",
    noEndDate: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const dictionary = useUnitsPropertiesDictionary();

  useEffect(() => {
    if (opened) {
      setSelectedActiveDiscount(null);
      setCustomDiscount({
        code: `CUSTOM-${Date.now()}`,
        type: "flat",
        value: "",
        startDate: new Date().toISOString().split("T")[0],
        endDate: "",
        noEndDate: false
      });
      setErrors({});
    }
  }, [opened]);

  useEffect(() => {
    if (opened)
      if (permissions.canApplyActiveDiscounts && activeDiscounts.length > 0)
        setActiveTab("active");
      else if (permissions.canApplyCustomDiscounts) setActiveTab("custom");
  }, [
    opened,
    permissions.canApplyActiveDiscounts,
    permissions.canApplyCustomDiscounts
  ]);

  const getInputPlaceholder = (type: DiscountType): string => {
    if (type === "flat") return "Amount";
    if (type === "percentage") return "Percent";
    return "100%";
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (customDiscount.type === "flat") {
      if (!customDiscount.value || Number(customDiscount.value) <= 0)
        newErrors.value = "Amount is required and must be greater than 0";
    } else if (customDiscount.type === "percentage") {
      const percentValue = Number(customDiscount.value);
      if (!customDiscount.value || percentValue <= 0)
        newErrors.value = "Percentage is required and must be greater than 0";
      else if (percentValue > 100)
        newErrors.value = "Percentage cannot be greater than 100";
    }

    if (!customDiscount.noEndDate && !customDiscount.endDate)
      newErrors.endDate =
        "End date is required unless 'no end date' is selected";

    if (
      !customDiscount.noEndDate &&
      customDiscount.endDate &&
      !moment(customDiscount.endDate).isAfter(moment().startOf("day"))
    )
      newErrors.endDate = "End date must be after today";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDiscountAmount = () => {
    if (customDiscount.type === "free") return 100;
    if (customDiscount.type === "flat")
      return Math.round(Number(customDiscount.value) * 100);
    return Number(customDiscount.value);
  };

  const handleApplyActiveDiscount = () => {
    if (selectedActiveDiscount) {
      onApplyDiscount(selectedActiveDiscount);
      onClose();
    }
  };

  const handleApplyCustomDiscount = () => {
    if (!validateForm()) return;

    const discount: CustomDiscount = {
      isCustom: true,
      code: customDiscount.code,
      discount_type: customDiscount.type,
      discount_amount: getDiscountAmount(),
      end_date: customDiscount.noEndDate ? undefined : customDiscount.endDate
    };

    onApplyDiscount(discount);
    onClose();
  };

  // const selectedDiscountType = discountTypeOptions.find(
  //   (option) => option.value === customDiscount.type
  // );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="w-full max-w-[500px]"
    >
      <div className="flex items-center justify-between mb-3 pb-4 p-6 border-b border-alpha-40">
        <div>
          <h2 className="text-base font-bold text-content-1000">
            Apply discount to application fee
          </h2>
          {applicantEmail && (
            <p className="text-content-400 text-sm mt-1">
              Applicant: {applicantEmail}
            </p>
          )}
        </div>
        <IconButton
          HugeIcon={Cancel01Icon}
          onClick={onClose}
          size={IconButtonSize.SM}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </div>

      <div className="pl-6">
        <Tabs
          variant="underline"
          tabs={["active", "custom"]}
          displayMap={{
            active: (
              <Tooltip>
                <TooltipTrigger asChild>Active discount codes</TooltipTrigger>
                <Show if={!permissions.canApplyActiveDiscounts}>
                  <TooltipContent className="max-w-[480px] rounded-lg z-[50] bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                    You don&apos;t have permission to active discount codes.
                    Please contact an administrator
                  </TooltipContent>
                </Show>
                <Show
                  if={
                    permissions.canApplyActiveDiscounts &&
                    activeDiscounts.length === 0
                  }
                >
                  <TooltipContent className="max-w-[480px] rounded-lg z-[50] bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                    There are currently no active discount codes for this
                    {dictionary.property}.
                  </TooltipContent>
                </Show>
              </Tooltip>
            ),
            custom: (
              <Tooltip>
                <TooltipTrigger asChild>Custom discounts</TooltipTrigger>
                <Show if={!permissions.canApplyCustomDiscounts}>
                  <TooltipContent className="max-w-[480px] rounded-lg z-[50] bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                    You don&apos;t have permission to apply custom discounts.
                    Please contact an administrator
                  </TooltipContent>
                </Show>
              </Tooltip>
            )
          }}
          tabProps={{
            active: {
              disabled:
                !permissions.canApplyActiveDiscounts ||
                activeDiscounts.length === 0
            },
            custom: {
              disabled:
                !permissions.canApplyCustomDiscounts ||
                !permissions.canApplyActiveDiscounts
            }
          }}
          selected={activeTab}
          setActiveTab={(t) => {
            setActiveTab(t);
          }}
        />
      </div>

      <Show if={activeTab === "active"}>
        <div className="animate-appear">
          <div className="p-6 bg-background-900">
            {activeDiscounts.length ? (
              <>
                <p className="text-sm text-content-400 font-medium mb-4 pb-4 border-b border-alpha-40">
                  You can apply an active discount code available for the{" "}
                  {dictionary.property} on behalf of the applicant. This
                  discount will be applied automatically at checkout.
                </p>

                <div className="space-y-2 h-[250px] overflow-y-auto">
                  {activeDiscounts.map((discount: DiscountCode) => (
                    <DiscountRadioCard
                      key={discount.id}
                      discount={discount}
                      checked={selectedActiveDiscount?.id === discount.id}
                      onSelect={setSelectedActiveDiscount}
                      name="activeDiscount"
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-sm text-content-400 font-medium mb-4">
                  There are currently no active discount codes for this
                  {dictionary.property}.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end border-t border-alpha-100 gap-3 py-4 px-6">
            <Button
              type="button"
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={onClose}
            >
              Cancel
            </Button>
            {activeDiscounts.length > 0 ? (
              <Button
                type="button"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                onClick={handleApplyActiveDiscount}
              >
                Apply discount
              </Button>
            ) : (
              <Link to="/discounts">
                <Button
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                >
                  Add discount
                </Button>
              </Link>
            )}
          </div>
        </div>
      </Show>

      <Show if={activeTab === "custom" && permissions.canApplyCustomDiscounts}>
        <div className="animate-appear">
          <div className="p-6 bg-background-900">
            <p className="text-sm text-content-400 font-medium mb-4 pb-4 border-b border-alpha-40">
              You can apply a custom discount to the application fee. This
              discount will be applied automatically at checkout.
            </p>

            <form className="space-y-4">
              <fieldset>
                <div className="flex gap-4 items-end">
                  <div className="max-w-[150px] w-full">
                    <Select
                      className="!mb-0"
                      hideOptional
                      id="discountType"
                      label="Discount type"
                      value={customDiscount.type}
                      onChange={(e) => {
                        const value = e.target.value as DiscountType;
                        // Reset value when type changes, like in DiscountModal
                        if (value === "free")
                          setCustomDiscount({
                            ...customDiscount,
                            type: value,
                            value: 100
                          });
                        else
                          setCustomDiscount({
                            ...customDiscount,
                            type: value,
                            value: ""
                          });
                        // Clear any value errors when type changes
                        if (errors.value) setErrors({ ...errors, value: "" });
                      }}
                      options={discountTypeOptions}
                      error={errors.value}
                    />
                  </div>

                  <Input
                    hideErrorMessage
                    id="discountValue"
                    type={
                      customDiscount.type === "flat" ? "money" : "percentage"
                    }
                    value={customDiscount.value}
                    onChange={(e) => {
                      if (
                        customDiscount.type === "percentage" ||
                        customDiscount.type === "free"
                      ) {
                        const value = e.target.value.replace("%", "");
                        setCustomDiscount({
                          ...customDiscount,
                          value: Number(value)
                        });
                      } else
                        setCustomDiscount({
                          ...customDiscount,
                          value: Number(e.target.value)
                        });
                      if (errors.value) setErrors({ ...errors, value: "" });
                    }}
                    error={errors.value}
                    readOnly={customDiscount.type === "free"}
                    placeholder={getInputPlaceholder(customDiscount.type)}
                  />
                </div>

                {errors.value ? (
                  <span className="text-danger-500 text-xs">
                    {errors.value}
                  </span>
                ) : null}
              </fieldset>

              {/* End Date */}
              <div>
                <DateInput
                  id="endDate"
                  label="End date"
                  value={customDiscount.endDate}
                  onChange={(e) =>
                    setCustomDiscount({
                      ...customDiscount,
                      endDate: e.target.value
                    })
                  }
                  readOnly={customDiscount.noEndDate}
                  hideOptional
                  error={errors.endDate}
                />

                <div className="pt-4">
                  <Checkbox
                    id="noEndDate"
                    checked={customDiscount.noEndDate}
                    onChange={(e) =>
                      setCustomDiscount({
                        ...customDiscount,
                        noEndDate: e.target.checked,
                        endDate: e.target.checked ? "" : customDiscount.endDate
                      })
                    }
                    label="The discount doesn't have end date"
                  />
                </div>
              </div>
            </form>
          </div>{" "}
          {/* Action Buttons */}
          <div className="flex justify-end gap-2 py-4 px-6 border-t border-alpha-100">
            <Button
              type="button"
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              onClick={handleApplyCustomDiscount}
            >
              Apply discount
            </Button>
          </div>
        </div>
      </Show>
    </Modal>
  );
};
